export const microspaceAnalysisEn = {
  breadcrumb: {
    parent: "Performance Analysis",
    current: "Microspace",
  },
  pageTitle: "Performance Analysis",
  pageSubtitle:
    "Analyze category performance, identify actions, and generate simulations based on AI recommendations",
  configPrompt: {
    label: "Save Configuration",
    default: "Config",
    confirm: "Save",
    cancel: "Cancel",
  },
  bannerTitle: "Unlock Deep Category Insights",
  bannerDescription:
    "Start by configuring your filters and defining your store scope, individually or by cluster. Save your settings for future use, dive into your category performance analysis, and explore AI-powered recommendations. When you're ready, generate an assortment simulation to put insights into action.",
  buttons: {
    download: "Download",
    changeConfiguration: "Change configuration",
    generateAnalysis: "Generate Analysis",
    generateSimulation: "Generate Assortment Simulation",
    generating: "Generating...",
    exportActionPlan: "Export Action Plan",
    clearFilters: "Clear filters",
  },
  filters: {
    granularity: "Granularity",
    year: "Year",
    month: "Month",
    quarter: "Quarter",
    compareWith: "Compare With",
    geographicLevel: "Segmentation",
    commercialChain: "Commercial Chain",
    geo: "Geo Zone",
    allGeos: "All Zones",
    allClusters: "All Institutional Clusters",
    allStores: "All Stores",
    allClusterCategories: "All Cluster Categories",
    selectGeo: "Select Geo Zone...",
    selectCluster: "Select Cluster...",
    selectStore: "Select Store...",
    selectClusterCategory: "Select Cluster Category...",
    cluster: "Cluster",
    store: "Store",
    category: "Category",
    period: "Period",
    comparison: "Comparison",
    granularityOptions: {
      l12w: "Last 12 weeks",
      l24w: "Last 24 weeks",
    },
    geoLevelOptions: {
      general: "General",
      cluster: "Institutional Cluster",
      clusterCategory: "Cluster Category",
      store: "Store",
    },
    clusterCategory: "Cluster Category",
    select: "Select",
  },
  pogSizePicker: {
    title: "POG Size",
    selectSizes: "Select sizes...",
    allSizes: "All Sizes",
    selected: "{{count}} selected",
    noPogData: "No POG data for this scope — try a different store or category.",
  },
  emptyState: {
    title: "No analysis generated yet",
    description:
      'Configure your filters above and click "Generate Analysis" to view detailed performance insights.',
    howToStart: "How to get started:",
    title2: "Ready to Generate Your Analysis",
    description2:
      'Configure your filters above and click "Generate Analysis" to unlock detailed performance insights',
    quickStartGuide: "Quick Start Guide",
    step1Prefix2: "Define your ",
    step1Bold2: "commercial scope",
    step1Suffix3: "",
    step2Prefix3: "Apply your ",
    step2Bold3: "geographic and time",
    step2Suffix3: " filters",
    step3Prefix3: "Click ",
    step3Bold3: "Generate Analysis",
    step3Suffix3: " to explore your results",
    savedConfigs: "Saved Configurations",
    savedConfigsDesc: "Quick access to your settings",
    save: "Save",
    noSavedConfigs: "No saved configurations yet",
    noSavedConfigsDesc: "Save your current filter settings for quick access",
    lastUsed: "Last used",
  },
  savedConfigs: {
    confirmDelete: {
      title: "Delete saved configuration?",
      description: 'This will permanently remove "{{name}}". This action cannot be undone.',
      cancel: "Cancel",
      confirm: "Delete",
    },
  },
  tabs: {
    navigationLabel: "Microspace analysis sections",
    analysis: "ANALYSIS",
    analysisSubtitle: "How is it performing and why?",
    action: "ACTION",
    actionSubtitle: "What should I do?",
    majorAction: "MAJOR ACTIONS",
    majorActionSubtitle: "Structural assortment changes",
    minorAction: "MINOR ACTIONS",
    minorActionSubtitle: "Adds, deletes and swaps",
  },
  chapterWrapper: {
    chapterLabel: "CHAPTER {{number}}",
    collapse: "Collapse",
    collapseAll: "Collapse all",
    expandAll: "Expand all",
  },
  chapterExport: {
    pdf: "PDF",
    excel: "Excel",
    errorTitle: "Export failed",
    errorMessage: "Could not generate the file. Please try again.",
  },
  applicability: {
    pogUnresolvedBanner: {
      title: "Some stores have unresolved planograms",
      description:
        "{{count}} store(s) cannot be included in actions because their planograms are unresolved: {{stores}}.",
      andMore: "and {{count}} more",
    },
  },
  storesTable: {
    title: "Stores in Analysis",
    subtitle: "{{count}} stores included based on current filters",
    loading: "Loading stores in analysis...",
    loadFailed: "Could not load stores in analysis.",
    retry: "Retry",
    pogSizeCount: "{{count}} POG size",
    pogSizeCountPlural: "{{count}} POG sizes",
    columns: {
      storeId: "Store ID",
      storeName: "Store Name",
      cluster: "Cluster",
      pogSize: "POG Size",
      planogram: "Planogram",
    },
    clusterStores: "{{name}} Stores",
    avgPog: "Avg POG: {{value}} segments",
    avgPogEmpty: "Avg POG: - segments",
  },
  actions: {
    loadFailed: "Could not load action recommendations.",
    retry: "Retry",
  },
  chapters: {
    executiveSummary: {
      title: "Executive Summary",
      subtitle: "Health Score Overview",
      healthScore: "Health Score",
      healthScoreTooltip: {
        title: "Health Score Calculation",
        description:
          "The Health Score measures overall performance by evaluating each KPI against its target goal, then weighting them together into a final score.",
        formulaLabel: "Formula",
        formula: "Health Score = Σ(KPI_Score × Weight) / Σ(Weights)",
        exampleLabel: "Example",
        exampleSales: "Sales Score: 95 × 30% = 28.5",
        exampleUnits: "Units Score: 105 × 25% = 26.3",
        exampleMargin: "Margin Score: 88 × 30% = 26.4",
        exampleDos: "DOS Score: 110 × 15% = 16.5",
        exampleFinal: "Final = 97.7",
        scoring:
          "Individual KPI Score: (Current / Target) × 100. Scores ≥100 = On Target, 90–99 = Near Target, <90 = Below Target.",
      },
      analysisLabel: "ANALYSIS",
      metrics: {
        sales: "Sales",
        unitMovement: "Unit Movement",
        margin: "Margin",
        inventory: "Inventory",
        dos: "DOS",
      },
      status: {
        onTarget: "On Target",
        nearTarget: "Near Target",
        belowTarget: "Below Target",
      },
      analysis: {
        excellent: "Excellent performance.",
        showingStrong: "showing strong results",
        wellPositioned: "The category is well-positioned for continued growth.",
        nearTarget: "Near target performance.",
        attentionNeeded: "Attention needed on",
        smallAdjustments: "Small adjustments could move the category to target levels.",
        belowTarget: "Below target performance.",
        focusRequired: "Focus required on",
        strategicInterventions: "Strategic interventions recommended to close performance gaps.",
        significantGaps: "Significant gaps detected.",
        priorityActions: "Priority actions needed for",
        comprehensiveReview: "Comprehensive review and corrective action plan recommended.",
      },
      editWeights: "Edit Weights",
      editWeightsDesc: "Adjust the weight of each metric in the Health Score calculation",
      editTargets: "Edit Targets",
      editTargetsDesc: "Set target growth rates for performance metrics",
      healthContribution: "Health Contribution",
      weight: "weight",
      score: "Score",
      previous: "Previous",
      target: "Target",
      current: "Current",
      progressToTarget: "Progress to Target",
      totalWeight: "Total Weight",
      weightsInfo:
        "Weights determine each metric's contribution to the overall Health Score. They will be automatically normalized in the calculation.",
      targetsInfo:
        "These percentages represent expected growth vs the comparison period. They are used to calculate target values and determine if performance meets expectations.",
      inventoryNote: "Negative values indicate target reduction in Days On Inventory",
      cancel: "Cancel",
      saveWeights: "Save Weights",
      saveTargets: "Save Targets",
    },
    metricDeepDive: {
      title: "Metric Deep Dive",
      subtitle: "KPI Trends",
      summaryText:
        "Commercial performance shows positive momentum with sales value growing {{salesGrowth}} and gross margin expanding {{marginGrowth}}. Inventory health remains optimal at {{doi}} DOI.",
      metricLabels: {
        salesValue: "Sales Value",
        unitsSold: "Units Sold",
        grossMargin: "Gross Margin",
        activeSkuCount: "Active SKU Count",
        inventoryTurnover: "Inventory Turnover",
        daysOfInventory: "Days of Inventory",
        outOfStock: "Out of Stock (%)",
        pareto: "Pareto 80/20",
      },
      kpiTooltips: {
        salesValue: "Total revenue generated from product sales in the selected period.",
        unitsSold:
          "Total number of units sold across all SKUs in the category during the selected period.",
        grossMargin:
          "Gross profit as a percentage of sales. Calculated as (Sales − Cost) / Sales × 100.",
        activeSkuCount:
          "Number of distinct SKUs with at least one unit sold in the selected period.",
        inventoryTurnover:
          "How many times inventory is sold and replaced per year. Higher values indicate more efficient inventory management.",
        daysOfInventory:
          "Average number of days of stock on hand based on current sales rate. Lower values indicate faster-moving inventory.",
        outOfStock:
          "Percentage of SKU-location combinations with zero units available. Lower values indicate better product availability.",
        pareto:
          "Percentage of SKUs that generate 80% of total sales. Lower values indicate a more concentrated, efficient assortment.",
      },
      chartTitle: "Category Performance Trend",
      chartTooltip: "Shows sales evolution over time comparing current vs previous period.",
      currentPeriod: "Current Period",
      periodTrend: "Period Trend",
      previousYear: "Previous Year",
      previousPeriod: "Previous Period",
      currentPeriodGrowth: "Current Period Growth",
      previousGrowth: "{{name}} Growth",
      targetLegend: "Target (+{{percent}}%)",
      vsTarget: "vs Target (+{{percent}}%)",
      units: {
        days: "{{value}} days",
        skus: "{{value}} SKUs",
      },
      growthBridge: {
        title: "Growth Bridge Analysis",
        description:
          "The growth of <strong>{{total}}%</strong> is driven by <strong>Volume ({{volume}}%)</strong> while price contributes little ({{price}}%).",
        tooltip: "Shows how the category grew, broken down by Price, Volume, and Mix effects.",
        noData: "No growth bridge data available.",
        priceEffect: "Price Effect",
        volumeEffect: "Unit Movement",
        mixEffect: "Mix Effect",
        ofGrowth: "{{percent}}% of growth",
        neutral: "Neutral",
        axisSales: "Sales ($K)",
      },
      narrative: {
        commercialPerformance: "Commercial performance shows {{tone}} with {{details}}.",
        tonePositive: "positive momentum",
        toneNegative: "downward pressure",
        toneMixed: "mixed results",
        salesGrowing: "sales value growing {{delta}} to {{value}}",
        salesDeclining: "sales value declining {{delta}} to {{value}}",
        salesFlat: "sales at {{value}}",
        marginExpanding: "gross margin expanding {{delta}} to {{value}}",
        marginContracting: "gross margin contracting {{delta}} to {{value}}",
        marginStable: "gross margin at {{value}}",
        spaceEfficiency:
          " Space-to-Sales Index stands at {{value}}, indicating {{efficiency}} across the assortment.",
        efficiencyStrong: "strong space productivity",
        efficiencyModerate: "moderate space efficiency",
        efficiencyWeak: "space underperformance",
        conjunction: "and",
      },
      kpiCorrelation: {
        title: "KPI Correlation Matrix",
        tooltip:
          "Shows the statistical relationship between key performance indicators. |r| values above 0.7 indicate a strong correlation.",
        correlationStrength: "Correlation Strength",
        positive: "Positive",
        moderateNegative: "Moderate Negative",
        strongNegative: "Strong Negative",
        strongThreshold: "|r| > 0.7 = Strong",
        kpiLabels: {
          space_share: "Space Share",
          sales_share: "Sales Share",
          facings: "Facings",
          sales_pesos: "Sales Pesos",
          units: "Units",
          space_to_sales_index: "Space To Sales Index",
          gm_pesos: "Gm Pesos",
        },
      },
    },
    decomposition: {
      title: "Segmentation Decomposition",
      subtitle: "Segmentation Analysis",
      description:
        "Space allocation across {{entity}} reveals efficiency differences. Compare sales share against space share to surface over- and under-performing groups.",
      entityPlural: {
        cluster: "clusters",
        store: "stores",
        categoryCluster: "category clusters",
      },
      linearSpace: "Linear Space by {{entity}}",
      salesPercent: "Sales %",
      spacePercent: "Space %",
      efficiency: "Efficiency: {{value}}x",
      columns: {
        cluster: "Cluster",
        store: "Store",
        categoryCluster: "Category Cluster",
        sales: "Sales",
        units: "Units",
        margin: "Margin",
        growth: "Growth",
        linear: "Linear",
        efficiencyCol: "Efficiency",
      },
      allocationBaseline: "Allocation baseline",
      allocationBaselineDesc:
        "Total linear space is {{space}} across {{count}} {{entity}} with {{sales}} in total sales.",
      waterfallTitle: "Growth Contribution Waterfall Analysis",
      waterfallNoData: "No waterfall data available for selected filters",
      waterfallAxisLabel: "Growth Contribution (%)",
      tooltipContribution: "Contribution",
      tooltipCumulative: "Cumulative",
      ofGrowth: "{{percent}}% of growth",
      totalLabel: "Total",
      totalGrowth: "Total growth",
      positiveLabel: "Positive:",
      negativeLabel: "Negative:",
      netLabel: "Net:",
      strategicInsight: "Strategic Insight",
      scatterTitle: "Performance Dynamics: Sales Volume vs Growth Rate",
      scatterNoData: "No scatter data available for selected filters",
      scatterAxisSales: "Sales ($K)",
      scatterAxisGrowth: "Growth %",
      tooltipSales: "Sales",
      tooltipGrowth: "Growth",
      tooltipMargin: "Margin",
      tooltipUnits: "Units",
      heatmapTitle: "Space Allocation Gaps & Optimization Opportunities (Space vs Sales)",
      heatmapNoData: "No space allocation gap data available",
      heatmapDefaultDesc:
        "Compares space share vs sales share by cluster. Green cells indicate under-allocation (space < sales — expansion opportunity), while red cells indicate over-allocation (space > sales — reduction opportunity).",
      viewBy: "View by",
      toggleBrand: "Brand",
      toggleSubcategory: "Subcategory",
      actionableRecommendation: "Actionable Recommendation",
      actionableRecommendationText:
        "Reduce space allocation for over-spaced brands (red zones) while expanding under-spaced brands in key formats (green zones). This zero-cost reallocation requires only planogram adjustments and can deliver incremental category sales.",
      chartTooltips: {
        linearSpace:
          "Current shelf space distribution by store format. This represents the total linear space available for the category across all locations in the analysis.",
        waterfall:
          "Incremental growth contribution by cluster format. Each bar represents the percentage point addition to total category performance, revealing which formats are driving business momentum.",
        scatter:
          "X-axis: Total Sales | Y-axis: Growth % | Bubble size: Margin %. Top-right quadrant = optimal performance (high sales + high growth). Larger bubbles indicate stronger margin performance.",
        heatmap:
          "Space allocation gaps across store formats. Green cells indicate under-allocation (expansion opportunity), while red cells indicate over-allocation (reduction opportunity). Values represent percentage points above or below optimal space allocation.",
      },
      heatmap: {
        title: "Space Gap Heatmap",
        description:
          "Brand performance gaps by cluster. Green = under-spaced (opportunity), Red = over-spaced (rebalance).",
        premium: "Premium",
        urban: "Urban",
        suburban: "Suburban",
      },
      legend: {
        veryUnderSpaced: "Very Under-spaced",
        underSpaced: "Under-spaced",
        slightlyUnder: "Slightly Under",
        balanced: "Balanced",
        slightlyOver: "Slightly Over",
        overSpaced: "Over-spaced",
        veryOverSpaced: "Very Over-spaced",
      },
      narrative: {
        descriptionSingle: '"{{cluster}}" accounts for 100% of sales and space.',
        description:
          "Space allocation across {{entity}} reveals efficiency differences. {{most}} generates {{mostSales}}% of sales with {{mostSpace}}% of space ({{mostEff}}pp efficiency), while {{least}} shows {{leastEff}}pp{{rebalance}}.",
        rebalanceHint: " — an opportunity for rebalancing",
        waterfallDesc:
          "{{top}} drives {{pp}}pp of growth ({{pct}}% of total gain), followed by {{rest}}. The distribution reveals efficiency disparities across {{entity}}.",
        waterfallFollowed: "{{cluster}} at {{pp}}pp",
        waterfallInsight:
          "{{top}} format represents {{spacePct}}% of total space yet delivers {{growthPct}}% of total growth, demonstrating superior space productivity. Each linear meter in {{top}} generates disproportionately higher returns than other formats.",
        scatterShows:
          "{{cluster}} shows {{growth}} growth with {{margin}}% margin and {{sales}} in sales",
        insightGrowthLeader: "Growth leader with strong margin performance",
        insightVolumeLeader: "Volume leader with scale advantage",
        insightUnderperforming: "Underperforming — needs space reallocation",
        insightStable: "Stable performer with expansion opportunity",
        heatmapDesc:
          "{{under}} shows critical under-allocation ({{underGap}}pp avg gap), while {{over}} demonstrates consistent over-allocation ({{overGap}}pp). Redistributing space from over-spaced to under-spaced {{entity}} creates immediate value.",
        brands: "brands",
        subcategories: "subcategories",
      },
    },
    brandAnalysis: {
      title: "Subcategory & Brand Detail",
      subtitle: "Brand Mix",
      spaceEfficiencyTitle: "Space Efficiency",
      spaceEfficiencySubtitle: "How does shelf space align with sales performance?",
      gapAnalysisTitle: "Gap Analysis",
      contributionToGrowthTitle: "Contribution to Growth",
      contributionToGrowthSubtitle: "Which items are driving or dragging category performance",
      growthDriversAndGapsTitle: "Growth Drivers & Decliners",
      growthDriversSubtitle:
        "Which items are accelerating growth and which are holding the category back",
      performanceShiftTitle: "Performance Shift",
      performanceShiftSubtitle: "How the sales mix is shifting across items",
      subcategorySpaceEfficiency: "1. Subcategory Space Efficiency",
      subcategorySpaceEfficiencySubtitle: "How does shelf space align with sales performance?",
      subcategoryGapAnalysis: "Subcategory Gap Analysis",
      brandSpaceEfficiency: "2. Brand Space Efficiency",
      brandSpaceEfficiencySubtitle: "Which brands deserve more or less shelf space based on sales?",
      brandGapAnalysis: "Brand Gap Analysis",
      contributionToGrowthBrand: "Brand Contribution to Growth",
      contributionToGrowthSubcategory: "Subcategory Contribution to Growth",
      contributionToGrowthSubtitleBrand:
        "Which brands are driving or dragging category performance",
      contributionToGrowthSubtitleSubcategory:
        "Which subcategories are contributing to category growth",
      toggleBrand: "Brand",
      toggleSubcategory: "Subcategory",
      growthDriversAndGapsBrand: "Brand Growth Drivers & Decliners",
      growthDriversAndGapsSubcategory: "Subcategory Growth Drivers & Decliners",
      growthDriversSubtitleBrand:
        "Which brands are accelerating growth and which are holding the category back",
      growthDriversSubtitleSubcategory:
        "Which subcategories are driving category expansion and which are declining",
      growthDrivers: "Growth Drivers",
      topPerformers: "Top Performers",
      performanceGaps: "Performance Gaps",
      biggestDecliners: "Biggest Decliners",
      performanceShiftSubcategory: "Subcategory Performance Shift",
      performanceShiftBrand: "Brand Performance Shift",
      performanceShiftSubtitleSubcategory: "Customer preference evolution across sauce types",
      performanceShiftSubtitleBrand: "Market share shifts across competing brands",
      previousPeriodLegend: "Previous Period",
      currentPeriodLegend: "Current Period",
      tooltipScatterPlot:
        "Points above the line are under-spaced (need more shelf space). Points below the line are over-spaced (reduce shelf space). Points on the line are perfectly balanced.",
      tooltipContribution:
        "Shows incremental sales contribution. Green bars indicate growth drivers, red bars show declining items.",
      tooltipDriversGaps:
        "Identifies which items are accelerating growth and which are holding the category back, with impact quantification.",
      tooltipPerformanceShift:
        "Shows how sales mix is shifting between items, revealing emerging consumer trends.",
      noData: "No brand or subcategory data available for this category.",
      noDataSection: "No data available.",
      filterTopN: "Show",
      topNAll: "All",
      topNCount: "Top {{count}}",
      filterGroups: "Groups",
      filterGroupsPlaceholder: "All groups",
      filterGroupsAll: "All groups",
      contributionAxisSales: "Sales Change ($K)",
      contributionLabel: "Contribution",
      positiveLabel: "Positive:",
      negativeLabel: "Negative:",
      netLabel: "Net:",
      netItem: "Net",
      metricLabels: {
        sales: "Sales",
        units: "Units",
        margin: "Margin",
      },
    },
    productLevel: {
      title: "Product-Level Performance",
      subtitle: "SKU Analysis",
      newLaunches: "TOP SALES PERFORMANCE",
      newLaunchesValue: "+$25.7K from 3 SKUs",
      newLaunchesDesc: "Contributing 1.4% incremental growth",
      discontinued: "LOWEST SALES PERFORMANCE",
      discontinuedValue: "-$27.8K from 2 SKUs",
      discontinuedDesc: "Low performers exited the assortment",
      skuPerformanceMatrix: "SKU Performance Matrix — Bubble Chart",
      skuMatrixSubtitle:
        "Evaluate each SKU across sales, margin, and volume to identify Winners, Traffic Drivers, Opportunities, and Exit candidates",
      skuMatrixTooltipTitle: "How Quadrant Classification Works",
      skuMatrixTooltipText:
        "SKUs are classified into quadrants based on their position relative to the median values of the selected metrics. Winners: above median on both axes. Traffic Drivers: above X median, below Y. Opportunities: below X, above Y. Review/Exit: below both.",
      filterBy: "Filter by",
      filterAll: "All",
      filterBrand: "Brand",
      filterSubcategory: "Subcategory",
      selectBrand: "Select brand...",
      selectSubcategory: "Select subcategory...",
      chartConfig: "Chart Configuration",
      resetToDefault: "Reset to Default",
      xAxisMetric: "X-Axis Metric",
      yAxisMetric: "Y-Axis Metric",
      bubbleSize: "Bubble Size",
      axisOptions: {
        sales: "Sales Value ($)",
        margin: "Gross Margin (%)",
        units: "Units Sold",
      },
      axisLabels: {
        sales: "Sales Value ($K)",
        margin: "Gross Margin (%)",
        units: "Units Sold",
        salesIndex: "Sales Index",
        marginIndex: "Margin Index",
      },
      quadrantTitle: "Quadrant Classification & Root Cause Analysis",
      quadrants: {
        winners: "Winners",
        winnersDesc: "High sales & high margin",
        trafficDrivers: "Traffic Drivers",
        trafficDriversDesc: "High sales, lower margin",
        opportunities: "Opportunities",
        opportunitiesDesc: "High margin, low sales",
        reviewExit: "Review / Exit",
        reviewExitDesc: "Low on both axes",
      },
      tooltipLabels: {
        sales: "Sales",
        margin: "Margin",
        units: "Units",
      },
      skusCount: "({{count}} SKUs)",
      penetration: {
        title: "SKU Distribution & Performance Matrix",
        subtitle:
          "Each point is a SKU, plotted by store distribution and per-store sales velocity. Bubble size = revenue.",
        tooltip:
          "For each SKU: how many stores range it (X) versus its average units sold per store (Y). Bubble size is total revenue. The threshold lines are the category medians; colors band SKUs by sales-velocity quartile.",
        noData: "No SKU distribution data available.",
        toastTitle: "Ch5 · Penetration",
        loadError: "Could not load penetration data — no data shown",
        axisX: "Number of Stores Carrying SKU",
        axisY: "Sales per Store (units)",
        distributionThreshold: "Distribution Threshold",
        performanceThreshold: "Performance Threshold",
        skusCount: "{{count}} SKUs",
        tiers: {
          high: "High Performer",
          good: "Good",
          average: "Average",
          low: "Low",
        },
        tierRange: {
          high: "≥ {{min}} u/store",
          band: "{{min}}–{{max}} u/store",
          low: "< {{max}} u/store",
        },
        tooltipLabels: {
          brand: "Brand",
          stores: "Stores",
          unitsPerStore: "Units/store",
          revenue: "Revenue",
        },
        quadrantTitle: {
          star: "⭐ Star Performers",
          expansion: "📈 Expansion Opportunities",
          rationalize: "⚠️ Rationalization Candidates",
          test: "🧪 Test & Learn",
        },
        quadrantDesc: {
          star: "High distribution + high velocity. Category winners — protect space and stock.",
          expansion:
            "Strong per-store velocity but narrow distribution. Expand these into more stores.",
          rationalize:
            "Wide distribution but low velocity. Review for SKU optimization or delisting.",
          test: "Limited distribution and velocity. Monitor specialty and regional test SKUs.",
        },
      },
      cpiRanking: {
        title: "CPI Ranking vs Previous Period",
        subtitle:
          "Track product momentum to identify rising stars and declining performers before they become critical issues",
        description: "Combined Performance Index — weighted by Sales, Margin, and Rotation.",
        tooltipTitle: "CPI Ranking",
        tooltipText:
          "CPI (Category Performance Index) is a composite score combining sales, margin, and rotation. Green arrows indicate improving products, red arrows show declining products. Edit weights to simulate scenarios.",
        cpiWeightsTitle: "CPI Component Weights",
        salesWeight: "Sales Weight",
        marginWeight: "Margin Weight",
        rotationWeight: "Rotation Weight",
        total: "Total",
        mustEqual100: "(must equal 100%)",
        recalculate: "Recalculate CPI",
        narrative: {
          contributing: "Contributing {{amount}} from {{count}} SKU",
          contributingPlural: "Contributing {{amount}} from {{count}} SKUs",
          noNewLaunches: "No new launches detected in this period",
          flaggedForReview: "{{count}} SKUs flagged for review",
          lowSalesMargin: "Low sales + low margin — candidates for delisting",
          rankDetail: "Rank #{{rank}}",
          reviewQuadrant: "Review quadrant",
        },
        columns: {
          rank: "#",
          sku: "SKU",
          cpi: "CPI",
          spacePercent: "Space %",
          status: "Status",
          prev: "Prev",
          chg: "Chg",
          sales: "Sales",
          margin: "Margin",
          rotation: "Units",
        },
      },
    },
    actionRecommendations: {
      title: "Action Recommendations",
      subtitle: "Actionable Insights",
      description:
        "Based on comprehensive analysis, we've identified <strong>key opportunities</strong> across clusters. Click on recommendations below to automatically apply them in <strong>Chapter 2: Assortment & Space Actions</strong>.",
      impactLabel: "Impact:",
      skuActions: "{{count}} SKU actions",
      applied: "Applied",
      accept: "Accept",
      noData: "No cluster action recommendations available for this category.",
    },
    actionDetail: {
      title: "Action Detail",
      subtitle: "SKU Decisions",
      summary: {
        accepted: "Accepted",
        rejected: "Rejected",
        pending: "Pending",
        estImpact: "Est. Impact",
      },
      filterByCluster: "Filter by Cluster:",
      allClusters: "All Clusters",
      skuRecommendations: "SKU Recommendations",
      columns: {
        sku: "SKU",
        brand: "Brand",
        subcategory: "Subcategory",
        segment: "Segment",
        cpi: "CPI",
        quadrant: "Quadrant",
        cluster: "Cluster",
        store: "Store",
        recommendation: "Recommendation",
        rationale: "Rationale",
        decision: "Decision",
      },
      decisions: {
        yes: "Yes",
        accept: "Accept",
        no: "No",
        reject: "Reject",
        watch: "Watch",
        noAction: "No action",
        accepted: "Accepted",
        rejected: "Rejected",
        pending: "Pending",
      },
      quadrants: {
        winner: "Winner",
        opportunity: "Opportunity",
        traffic: "Traffic",
        review: "Review",
      },
      recommendations: {
        expand: "EXPAND",
        reduce: "REDUCE",
        flagged: "FLAGGED (Review)",
        protect: "PROTECT",
        monitor: "MONITOR",
        keepAsIs: "KEEP AS IS",
        expandFacing: "EXPAND +{{count}} facing",
        expandFacings: "EXPAND +{{count}} facings",
        reduceFacing: "REDUCE {{count}} facing",
        reduceFacings: "REDUCE {{count}} facings",
      },
      clearFilters: "Clear all",
      selected: "selected",
      acceptSelected: "Accept Selected",
      rejectSelected: "Reject Selected",
      viewOnly: "View only",
      notActionable: "Not actionable",
      noCh1Selection: "No recommendations accepted in Chapter 1",
      noCh1SelectionHint:
        "Accept one or more action recommendations above to review individual SKU decisions here.",
    },
    inventoryTable: {
      title: "Inventory Recommendations",
      noData: "No inventory recommendations available for this category.",
      summary: {
        unitsToAdd: "Units to Add",
        unitsToRemove: "Units to Remove",
        netChange: "Net Change",
        highPriority: "High Priority",
      },
      columns: {
        sku: "SKU",
        brand: "Brand",
        cluster: "Cluster",
        store: "Store",
        current: "Current",
        target: "Target",
        change: "Change",
        doi: "DOI",
        oosRisk: "OOS Risk",
        action: "Action",
        priority: "Priority",
      },
      values: {
        keep: "KEEP",
        low: "Low",
        medium: "Medium",
        high: "High",
        overstock: "Overstock",
      },
    },
    spaceAllocation: {
      title: "Linear Space Allocation by Cluster",
      noData: "No space allocation data available for this category.",
      storesAvg: "{{stores}} stores · {{avg}}cm avg",
      before: "Before",
      after: "After",
      legend: {
        winners: "Winners",
        opportunities: "Opportunities",
        traffic: "Traffic",
        review: "Review",
      },
    },
    reviewSimulate: {
      title: "Review & Simulate",
      subtitle: "Execute",
      description:
        "You've accepted <strong>{{count}}</strong> SKU recommendations from Chapter 2. Review the consolidated impact below, simulate the changes in the Assortment Simulation module, or export the full action plan for offline review and stakeholder approval.",
      acceptedTitle: "Accepted Recommendations from Chapter 2",
      expandAction: "Expand {{count}} SKU",
      expandActionPlural: "Expand {{count}} SKUs",
      reduceAction: "Reduce {{count}} SKU",
      reduceActionPlural: "Reduce {{count}} SKUs",
      flaggedAction: "Flagged {{count}} SKU",
      flaggedActionPlural: "Flagged {{count}} SKUs",
      flaggedSuffix: " for review",
      noActionsTitle: "No Actions Accepted",
      noActionsDescription:
        "Please accept recommendations in Chapter 2 to review and simulate the impact.",
      projectedImpact: "Projected Impact",
      kpiColumns: {
        kpi: "KPI",
        currentBefore: "Current (Before)",
        projectedAfter: "Projected (After)",
        change: "Change",
      },
      kpiRows: {
        salesValue: "Sales Value",
        grossMarginPercent: "Gross Margin %",
        spaceEfficiency: "Space Efficiency",
        bottomSkusPercent: "Bottom SKUs %",
        skuProductivity: "SKU Productivity ($/SKU)",
        categoryHealthScore: "Category Health Score",
      },
      catalogOnlyDisclaimer:
        "Note: numbers may be negative because some accepted ADD products come from catalog with no sales history yet — their projected gain is not simulated.",
    },
  },
  minorActions: {
    chapter1Title: "Minor Actions Recommendations",
    chapter1Subtitle: "Cluster Summary",
    chapter1SubtitleStore: "Store Summary",
    chapter1SubtitleClusterCategory: "Cluster Category Summary",
    chapter1Description:
      "Review and approve targeted assortment adjustments by cluster. These changes optimize the mix through strategic additions, removals, and facing adjustments based on performance data and demand patterns.",
    chapter1DescriptionStore:
      "Review and approve targeted assortment adjustments by store. These changes optimize the mix through strategic additions, removals, and facing adjustments based on performance data and demand patterns.",
    chapter2Title: "Action Detail",
    chapter2Subtitle: "Individual Recommendations",
    chapter3Title: "Review & Simulate",
    chapter3Subtitle: "Execute",
    chapter3Description:
      "You've accepted {{count}} minor action recommendations. Review the consolidated impact below and generate an assortment simulation or export the action plan.",
    recommendations: "recommendations",
    products: "products",
    stores: "stores",
    accepted: "Accepted",
    rejected: "Rejected",
    pending: "Pending",
    acceptAll: "Accept All",
    rejectAll: "Reject All",
    acceptedActionsTitle: "Accepted Minor Actions",
    acceptedSummary: {
      counts:
        "{{addCount}} added · {{removeCount}} removed · {{expandCount}} expanded · {{reduceCount}} reduced",
    },
    noActionsTitle: "No Recommendations Accepted Yet",
    noActionsDescription:
      "Please accept recommendations in Chapter 1 or Chapter 2 to review the projected impact.",
    rationale: {
      fsi: "FSI",
      cpi: "CPI",
      space: "Space",
      catalogNoSales: "Product not on any store's planogram",
    },
    removalsColumn: "Removals",
    addsColumn: "Adds",
    adjustmentsColumn: "Facing Adjustments",
    actionLabels: {
      add: "ADD",
      remove: "REMOVE",
      adjust: "ADJUST",
      expand: "EXPAND",
      reduce: "REDUCE",
    },
    addSource: {
      sameCluster: "Same cluster",
      chainWide: "Chain-wide",
      catalogNoSales: "Catalog only",
    },
    detailTooltip: {
      sku: "SKU",
      fsi: "FSI",
      cpi: "CPI",
      space: "Space",
      projectedImpact: "Projected impact",
      currentFacings: "Current facings",
      recommendedFacings: "Facings to add",
      targetFacings: "Target facings",
      facingChange: "Facing change",
      width: "Width",
      subcategory: "Subcategory",
      brand: "Brand",
      segment: "Segment",
      size: "Size",
    },
    noPlanogramSectionTitle: "No planogram found for stores",
  },
  charts: {
    scatterPlot: {
      xLabel: "Space Allocation (%)",
      yLabel: "Sales Contribution (%)",
      tooltipSpace: "Space: {{value}}%",
      tooltipSales: "Sales: {{value}}%",
      tooltipGap: "Gap: {{value}}pp",
      underSpaced: "Under-spaced",
      overSpaced: "Over-spaced",
    },
    gapAnalysis: {
      columns: {
        name: "Name",
        spacePercent: "Space %",
        salesPercent: "Sales %",
        gap: "Gap",
        status: "Status",
      },
      statuses: {
        underSpaced: "Under-spaced",
        overSpaced: "Over-spaced",
        balanced: "Balanced",
      },
    },
  },
  clusterCategoryConfig: {
    title: "Cluster Category Configuration",
    subtitle: "Configure store groupings by category",
    loadingTitle: "Loading cluster category configuration",
    loadingDescription: "Fetching saved cluster groupings for each chain.",
    chainCategories: "{{chain}} Categories",
    searchCategoriesPlaceholder: "Search categories by name...",
    columns: {
      category: "CATEGORY",
      status: "STATUS",
      clusters: "CLUSTERS",
      stores: "STORES",
    },
    configured: "Configured",
    notConfigured: "Not configured",
    noCategoriesMatch: "No categories match your search",
    headerSubtitle: "{{chain}} — Cluster Category Configuration",
    saveConfiguration: "Save Configuration",
    availableStores: "Available Stores",
    chainStores: "{{chain}} Stores",
    searchStoresPlaceholder: "Search by name or ID...",
    storesSelected: "{{count}} store selected",
    storesSelectedPlural: "{{count}} stores selected",
    clear: "Clear",
    allAssigned: "All stores assigned to clusters",
    noStoresMatch: "No stores match your search",
    createdClusters: "Created Clusters ({{count}})",
    newCluster: "New Cluster",
    clusterNamePlaceholder: "Cluster name...",
    editName: "Edit name",
    create: "Create",
    addCount: "+ Add {{count}}",
    storesCount: "{{count}} stores",
    noClusters: "No clusters created yet",
    noClustersHint: 'Click "New Cluster" to get started',
    storeMeta: "Store {{id}} – {{geo}} – {{banner}}",
    storeMetaWithPog: "Store {{id}} – POG Size: {{pogSize}} – {{geo}} – {{banner}}",
    pogSizeNoData: "No Data",
  },
  notifications: {
    sampleDataTitle: "Using sample data",
    filtersLoadFailed: "Could not load filters from API",
    executiveHealthTitle: "Ch1 · Health Score",
    executiveHealthEmpty: "No category health data available — sample data shown",
    executiveHealthFailed: "Could not load category health data — sample data shown",
    metricKpiTitle: "Ch2 · KPI Data",
    metricKpiFailed: "Could not load KPI data — no data shown",
    growthBridgeTitle: "Ch2 · Growth Bridge",
    growthBridgeFailed: "Could not load growth waterfall — sample data shown",
    clusterSummaryTitle: "Ch3 · Cluster Summary",
    clusterSummaryFailed: "Could not load cluster summary — no data shown",
    clusterSummaryEmpty: "No cluster data available — sample data shown",
    clusterWaterfallTitle: "Ch3 · Cluster Waterfall",
    clusterWaterfallFailed: "Could not load cluster waterfall — no data shown",
    clusterScatterTitle: "Ch3 · Cluster Scatter",
    clusterScatterFailed: "Could not load cluster scatter — no data shown",
    skuMatrixTitle: "Ch5 · SKU Matrix",
    skuMatrixFailed: "Could not load SKU matrix data — no data shown",
    cpiRankingTitle: "Ch5 · CPI Ranking",
    cpiRankingFailed: "Could not load CPI ranking data — no data shown",
    cpiRecalculationFailed: "CPI recalculation failed",
  },
  errors: {
    simulationFailed: "Simulation Failed",
    simulationFailedMessage: "Could not generate the assortment simulation. Please try again.",
    clusterSaveFailed: "Save Failed",
    clusterSaveFailedMessage: "Could not save the cluster configuration. Please try again.",
    configSaveFailed: "Save Failed",
    configSaveFailedMessage: "Could not save the configuration. Please try again.",
    configDeleteFailed: "Delete Failed",
    configDeleteFailedMessage: "Could not delete the configuration. Please try again.",
    configLoadFailed: "Load Failed",
    configLoadFailedMessage: "Could not load saved configurations.",
  },
} as const;

export type MicrospaceAnalysisTranslations = typeof microspaceAnalysisEn;
